/**
 * Account projects from the app (docs/ux/workflow-simplification.md §E4).
 *
 * - New project: name + goal → a new private GitHub repo (default) or an existing remote →
 *   machines → ONE passkey approves a checkout statement naming every chosen machine → one
 *   device-signed `project.checkout` per machine, all carrying that statement.
 * - Add machine: the same statement flow for one more machine (1 click, 1 passkey).
 * - Add from a machine: create the account project, then `project.adopt` on that machine
 *   (device-signed, no passkey: the machine only binds a folder the user registered there).
 * Name, goal, remote and paths stay end-to-end encrypted; the statement carries ids and digests.
 */
import {
  type CheckoutStatement,
  checkoutRemoteDigest,
  createCheckoutChallenge,
  createCheckoutStatement,
  type WebAuthnAssertion,
} from '@viko/crypto'
import {
  type CommandKind,
  type CommandPayload,
  type MachineCandidate,
  type MachineCheckout,
  ProjectCheckoutResult,
} from '@viko/protocol'
import type { CommandOutcome, Machine, Project } from './types.js'

/** "Tide Clock" → "tide-clock" (repo + folder name). */
export function slugify(name: string): string {
  return (
    name
      .normalize('NFKD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 80) || 'project'
  )
}

/** Machines that report a checkout of this project (their own table is authoritative). */
export function checkoutsOf(
  projectId: string,
  machines: readonly Machine[],
): { machine: Machine; checkout: MachineCheckout }[] {
  return machines.flatMap((m) =>
    (m.checkouts ?? [])
      .filter((c) => c.projectId === projectId)
      .map((c) => ({ machine: m, checkout: c })),
  )
}

/** Remotes seen in any machine's report (for "Use existing"). */
export function knownRemotes(machines: readonly Machine[]): string[] {
  const out = new Set<string>()
  for (const m of machines) {
    for (const c of m.checkouts ?? []) if (c.remote) out.add(c.remote)
    for (const c of m.candidates ?? []) if (c.remote) out.add(c.remote)
  }
  return [...out].sort()
}

/** Unbound repos each machine found (setup offer, `viko project add`). */
export function candidatesOf(machines: readonly Machine[]) {
  return machines.flatMap((m) => (m.candidates ?? []).map((c) => ({ machine: m, candidate: c })))
}

export type RepoChoice =
  | { kind: 'create'; owner: string; name: string }
  | { kind: 'existing'; remote: string }

export interface NewProjectDraft {
  name: string
  goal: string
  repo: RepoChoice
  machineIds: string[]
}

/** Wizard defaults: all online machines, create a private repo under the author's `gh` user. */
export function newProjectDefaults(machines: readonly Machine[], name = ''): NewProjectDraft {
  const online = machines.filter((m) => m.online)
  const owner = online.map((m) => m.projectDefaults?.ghUser).find(Boolean) ?? ''
  return {
    name,
    goal: '',
    repo: { kind: 'create', owner, name: slugify(name) },
    machineIds: online.map((m) => m.id),
  }
}

/** Why Create is disabled ('' when ready). */
export function draftProblem(d: NewProjectDraft, machines: readonly Machine[]): string {
  if (!d.name.trim()) return 'Name the project.'
  if (d.repo.kind === 'create') {
    if (!/^[A-Za-z0-9][A-Za-z0-9-]{0,38}$/.test(d.repo.owner))
      return 'Choose the GitHub owner for the new repository.'
    if (!/^[A-Za-z0-9][A-Za-z0-9._-]{0,99}$/.test(d.repo.name)) return 'Name the repository.'
  } else if (!/^(https?:\/\/|git@|ssh:\/\/)\S+$/.test(d.repo.remote.trim()))
    return 'Paste the repository URL (https://… or git@…).'
  const online = new Set(machines.filter((m) => m.online).map((m) => m.id))
  if (!d.machineIds.some((id) => online.has(id))) return 'Pick at least one online machine.'
  if (d.machineIds.length > 8) return 'Pick at most 8 machines.'
  return ''
}

export interface CheckoutDeps {
  accountId(): Promise<string>
  deviceKeyId(): Promise<string>
  assert(challenge: Uint8Array): Promise<WebAuthnAssertion>
  dispatch<K extends CommandKind>(
    kind: K,
    machineId: string,
    payload: CommandPayload<K>,
  ): Promise<CommandOutcome>
  now(): number
}

export type CheckoutProgress =
  | { machineId: string; state: 'waiting' | 'working' }
  | { machineId: string; state: 'done'; result: ProjectCheckoutResult }
  | { machineId: string; state: 'error'; error: string }

/** Human progress label for one machine's row. */
export function progressLabel(p: CheckoutProgress, creating: boolean): string {
  if (p.state === 'error') return p.error
  if (p.state !== 'done')
    return p.state === 'waiting' ? 'Waiting' : creating ? 'Creating repo · cloning…' : 'Cloning…'
  const r = p.result
  switch (r.status) {
    case 'created':
      return r.configCommit ? 'Repo created · config committed' : 'Repo created'
    case 'cloned':
      return r.landingId
        ? 'Cloned · config landing'
        : r.configCommit
          ? 'Cloned · config committed'
          : 'Cloned'
    case 'adopted':
      return 'Adopted existing clone'
    case 'needs-gh-auth':
      return 'Sign in to GitHub on this machine (gh auth login)'
    case 'conflict':
      return r.reason ?? 'Already checked out from another remote'
    default:
      return r.reason ?? 'Checkout failed'
  }
}

/**
 * Build ONE statement for every machine, approve it with ONE passkey, then send one
 * `project.checkout` per machine (in parallel). The config author is the first machine.
 */
export async function requestCheckouts(
  deps: CheckoutDeps,
  input: {
    projectId: string
    machineIds: string[]
    repo: RepoChoice
    dirName: string
    name?: string
    goal?: string
    /** Draft + commit shared config on the first machine (new projects). */
    author?: boolean
    /** g5-setup: the shared config reviewed while scoping (the first machine commits it). */
    config?: string
  },
  onProgress: (p: CheckoutProgress) => void = () => {},
): Promise<{ statement: CheckoutStatement; results: CheckoutProgress[] }> {
  const machineIds = [...new Set(input.machineIds)]
  const target =
    input.repo.kind === 'create'
      ? {
          create: {
            provider: 'github' as const,
            owner: input.repo.owner,
            name: input.repo.name,
            visibility: 'private' as const,
          },
        }
      : { remote: input.repo.remote.trim() }
  const configAuthor = input.author || input.repo.kind === 'create' ? machineIds[0] : undefined
  const statement = createCheckoutStatement({
    accountId: await deps.accountId(),
    projectId: input.projectId,
    machineIds,
    remoteDigest: await checkoutRemoteDigest(target),
    ...(configAuthor ? { configAuthor } : {}),
    deviceKeyId: await deps.deviceKeyId(),
    now: deps.now(),
  })
  const assertion = await deps.assert(await createCheckoutChallenge(statement))
  for (const machineId of machineIds) onProgress({ machineId, state: 'waiting' })
  const results = await Promise.all(
    machineIds.map(async (machineId): Promise<CheckoutProgress> => {
      onProgress({ machineId, state: 'working' })
      let p: CheckoutProgress
      try {
        const out = await deps.dispatch('project.checkout', machineId, {
          projectId: input.projectId,
          statement,
          assertion,
          ...target,
          dirName: input.dirName,
          ...(input.name ? { name: input.name } : {}),
          ...(input.goal ? { goal: input.goal } : {}),
          // Only the config author commits it; the others never need the text.
          ...(input.config && machineId === configAuthor ? { config: input.config } : {}),
        })
        p = { machineId, state: 'done', result: ProjectCheckoutResult.parse(out.result) }
      } catch (e) {
        p = { machineId, state: 'error', error: e instanceof Error ? e.message : String(e) }
      }
      onProgress(p)
      return p
    }),
  )
  return { statement, results }
}

/** The remote a finished checkout reported (canonical), for the project entity. */
export function reportedRemote(results: readonly CheckoutProgress[]): string | undefined {
  for (const r of results) if (r.state === 'done' && r.result.remote) return r.result.remote
  return undefined
}

export const checkoutOk = (p: CheckoutProgress) =>
  p.state === 'done' && ['created', 'cloned', 'adopted'].includes(p.result.status)

/** Add-from-a-machine: adopt a candidate the user registered on that machine. */
export async function adoptCandidate(
  deps: Pick<CheckoutDeps, 'dispatch'>,
  create: (name: string, remote: string | null) => Promise<Project>,
  machine: Pick<Machine, 'id'>,
  candidate: MachineCandidate,
): Promise<{ project: Project; result: ProjectCheckoutResult }> {
  const project = await create(candidate.name, candidate.remote ?? null)
  const out = await deps.dispatch('project.adopt', machine.id, {
    projectId: project.id,
    localPath: candidate.path,
  })
  return { project, result: ProjectCheckoutResult.parse(out.result) }
}
